import React, { useEffect } from "react";
import { useImmer } from "use-immer";
import { useTranslation } from "react-i18next";
import Axios from "axios";
import { Button, Table } from "rsuite";

import constants from "utils/constants";
import { formatDateWithNameOfMonth } from "utils/date-helper";
import { getModalTableHeight } from "utils/measurement-helper";

import { getErrorMessage } from "api/defaults";
import { loadCustomerHistory } from "api/customers/customers-api";

import { CustomerHistory as History } from "store/customers/add-new-customer/add-new-customer-types";

import ModalContainer from "components/Common/Modal/ModalContainer";
import PopoverCell from "components/Common/PopoverCell/PopoverCell";

import "./CustomerHistory.less";

interface ComponentProps {
    customerId: string;
    isOpened: boolean;
    setModalLoaderVisibility: (visible: boolean) => void;
    setErrorResponseMessage: (message: string) => void;
    setIsCustomerHistoryOpened: (isOpened: boolean) => void;
}

const CustomerHistory: React.FC<ComponentProps> = ({
    customerId,
    isOpened,
    setModalLoaderVisibility,
    setErrorResponseMessage,
    setIsCustomerHistoryOpened,
}: ComponentProps) => {
    const cancelTokenSource = Axios.CancelToken.source();

    const { t } = useTranslation();

    const [history, setHistory] = useImmer<History[]>([]);

    const handleModalClose = () => {
        setIsCustomerHistoryOpened(false);
        cancelTokenSource.cancel();
    };

    const header = <h1>{t("customerDetails.history.title")}</h1>;
    const footer = (
        <>
            <Button
                className={"customer-history-modal__button"}
                type="submit"
                appearance="ghost"
                onClick={handleModalClose}
            >
                {t("customerDetails.history.close")}
            </Button>
        </>
    );

    const tableHeight = getModalTableHeight(history.length);

    const getCustomerHistory = () => {
        setModalLoaderVisibility(true);
        loadCustomerHistory(customerId, cancelTokenSource)
            .then(([payload, status]) => {
                if (payload && status === constants.statusCode.OK) {
                    setHistory([payload]);
                } else {
                    setErrorResponseMessage(
                        getErrorMessage(String(payload), status)
                    );
                }
            })
            .catch((error: Error) => {
                const axiosError = error;
                if (!Axios.isCancel(error)) {
                    setErrorResponseMessage(axiosError.message);
                }
            })
            .then(() => {
                setModalLoaderVisibility(false);
            });
    };

    useEffect(() => {
        if (isOpened && customerId) {
            getCustomerHistory();
        }
    }, [customerId, isOpened]);

    return (
        <>
            <ModalContainer
                className={"customer-history-modal__container"}
                open={isOpened}
                header={header}
                footer={footer}
                onClose={handleModalClose}
            >
                <Table
                    showHeader={true}
                    virtualized={true}
                    data={history}
                    height={tableHeight}
                >
                    <Table.Column flexGrow={2}>
                        <Table.HeaderCell>
                            {t(`customerDetails.history.createdBy`)}
                        </Table.HeaderCell>
                        <Table.Cell dataKey="creator">
                            {(rowData: History) => (
                                <PopoverCell
                                    dataKey="creator"
                                    rowData={rowData}
                                />
                            )}
                        </Table.Cell>
                    </Table.Column>

                    <Table.Column flexGrow={1.5}>
                        <Table.HeaderCell>
                            {t(`customerDetails.history.createdOn`)}
                        </Table.HeaderCell>
                        <Table.Cell dataKey="creationDate">
                            {(rowData: History) => (
                                <>
                                    {rowData.creationDate ? (
                                        <>
                                            {formatDateWithNameOfMonth(
                                                new Date(rowData.creationDate)
                                            )}
                                        </>
                                    ) : (
                                        <>{t(`unknown`)}</>
                                    )}
                                </>
                            )}
                        </Table.Cell>
                    </Table.Column>
                </Table>
            </ModalContainer>
        </>
    );
};

export default CustomerHistory;
